/**
 * Catálogo visual único dos eventos da Ordem de Serviço.
 *
 * RELEASE: 2026-10-18 v1.3.0 - Eventos do app do prestador (deslocamento e
 *          check-in) com título, ícone e localização (`getServiceOrderEventLocation`).
 * RELEASE: 2026-10-10 v1.2.1 - Parâmetro opcional `omitNote` em
 *          `describeServiceOrderEvent`: omite motivo/observação do texto
 *          principal quando a nota já aparece em caixa destacada (evita
 *          duplicidade na timeline da OS). Padrão false preserva Veículo/Cliente.
 * RELEASE: 2026-10-10 v1.2.0 - Helper `getServiceOrderEventNote`: separa o
 *          motivo/observação do evento para exibição em caixa destacada na
 *          timeline da OS (mesmo padrão da timeline do Cliente).
 * RELEASE: 2026-10-10 v1.1.0 - BR-OS-033: eventos explícitos de reagendamento e
 *          cancelamento de agendamento (com motivo/observação no detalhe).
 * RELEASE: 2026-10-09 v1.0.1 - Remove bordas dos badges e reforça contraste dos eventos de atenção.
 * RELEASE: 2026-10-09 v1.0.0 - Centraliza descrição, ícone, tom e rótulo.
 */
import {
  Activity,
  ArrowRightLeft,
  CalendarClock,
  CalendarX,
  CheckCircle2,
  ClipboardCheck,
  MapPin,
  Navigation,
  Pause,
  Play,
  Plus,
  UserCheck,
  type LucideIcon,
} from 'lucide-react';
import { formatStockDateTime } from '@/lib/stock-presentation';
import { googleMapsPointUrl, parseEventLocation } from '@/lib/providerPortalDisplay';
import { SERVICE_ORDER_STATUS_LABEL, type ServiceOrderStatus } from '@/hooks/useServiceOrders';
import type { ServiceOrderTimelineItem } from '@/hooks/useServiceOrderDetails';

export interface ServiceOrderEventPresentation {
  Icon: LucideIcon;
  tone: string;
  badgeTone: string;
  badgeLabel: string;
}

/**
 * Traduz o evento mantendo os valores relevantes visíveis.
 * v1.2.1: `omitNote` omite o motivo/observação do texto principal — usar quando
 * a nota já é exibida em caixa destacada (timeline da OS). Padrão false mantém
 * o texto completo para Veículo/Cliente.
 */
export function describeServiceOrderEvent(event: ServiceOrderTimelineItem, omitNote = false): string {
  const status = (value?: string | null) => value ? SERVICE_ORDER_STATUS_LABEL[value as ServiceOrderStatus] ?? value : '—';
  const checklistName = event.note?.split('|')[1] || 'Checklist';
  switch (event.event_type) {
    case 'created': return 'Criou a OS';
    case 'status': return `Situação: ${status(event.old_value)} → ${status(event.new_value)}`;
    case 'phase': {
      // BR-OS-022: note = "categoria_origem|categoria_destino" (eventos antigos não têm)
      // v1.3.0 do trigger: terceiro segmento opcional = observação do usuário
      const [fromCat, toCat, obs] = (event.note ?? '').split('|');
      const tag = (c?: string) => (c ? ` [${c}]` : '');
      return `Fase: "${event.old_value ?? '—'}"${tag(fromCat)} → "${event.new_value ?? '—'}"${tag(toCat)}${obs && !omitNote ? ` — ${obs}` : ''}`;
    }
    case 'provider': return `Prestador: ${event.old_value ?? '—'} → ${event.new_value ?? '—'}`;
    case 'responsible': return `Responsável: ${event.old_value ?? '—'} → ${event.new_value ?? '—'}`;
    case 'schedule': return `Agendamento: ${event.old_value ? formatStockDateTime(event.old_value) : '—'} → ${event.new_value ? formatStockDateTime(event.new_value) : '—'}`;
    // BR-OS-033: cancelamento e reagendamento explícitos, com motivo/observação no note
    case 'schedule_cancelled': return `Agendamento cancelado: ${event.old_value ? formatStockDateTime(event.old_value) : '—'}${event.note && !omitNote ? ` — Motivo: ${event.note}` : ''}`;
    case 'schedule_rescheduled': return `Reagendado: ${event.old_value ? formatStockDateTime(event.old_value) : '—'} → ${event.new_value ? formatStockDateTime(event.new_value) : '—'}${event.note && !omitNote ? ` — ${event.note}` : ''}`;
    case 'timer_paused': return `Pausou o cronômetro${event.note && !omitNote ? `: ${event.note}` : ''}`;
    case 'timer_resumed': return 'Retomou o cronômetro';
    // 2026-10-17: serviços do negócio na O.S. (BR-OS-046..049)
    case 'deal_linked': return event.new_value ? `Negócio vinculado: ${event.new_value}` : 'Negócio desvinculado';
    // Ação "Criar ordem de serviço" do Fluxo de trabalho (BR-OS-050)
    case 'created_by_workflow': return event.new_value ? `Criada pelo fluxo de trabalho (negócio ${event.new_value})` : 'Criada pelo fluxo de trabalho';
    case 'services_synced': return event.note ?? 'Serviços atualizados do negócio';
    case 'services_answered': return 'Informou os serviços executados';
    // BR-OS-054: edição dos itens pelo operador
    case 'service_added_manual': return `Adicionou item: ${event.new_value ?? '—'}`;
    case 'service_quantity_changed': return `Alterou quantidade de ${event.note ?? 'item'}: ${event.old_value ?? '—'} → ${event.new_value ?? '—'}`;
    case 'service_removed_manual': return `Removeu item: ${event.old_value ?? '—'}`;
    case 'service_notes_changed': return `Atualizou orientação de ${event.note ?? 'item'}`;
    case 'service_added_field': return `Adicionou serviço em campo: ${event.new_value ?? '—'}`;
    case 'service_field_reviewed': return `${event.note ?? 'Analisou serviço adicional'}: ${event.new_value ?? '—'}`;
    // BR-OS-017: Instalação aprovada com kit já instalado no veículo
    case 'install_override_approved': return `Aprovou instalação com kit já instalado${event.note && !omitNote ? `: ${event.note}` : ''}`;
    case 'checklist_stage_released': return `Liberou a etapa de checklist: ${event.new_value ?? '—'}`;
    case 'checklist_status': return `${checklistName}: ${event.old_value ?? '—'} → ${event.new_value ?? '—'}`;
    // v1.3.0: eventos do app do prestador (localização aparece em linha própria)
    default:
      // v1.3.0: eventos do app do prestador (tipos fora da união tipada)
      if ((event.event_type as string) === 'provider_travel_started') return 'Deslocamento iniciado';
      if ((event.event_type as string) === 'provider_check_in') return 'Check-in realizado';
      return 'Alteração';
  }
}

/**
 * v1.2.0: separa o motivo/observação do evento (quando houver) para a timeline
 * da OS exibi-lo em caixa destacada, igual à timeline do Cliente.
 * Retorna null quando o evento não tem nota — nesse caso o detalhe segue em
 * linha discreta única. `describeServiceOrderEvent` permanece intacto para os
 * consumidores que exibem o texto completo (Veículo/Cliente).
 */
export function getServiceOrderEventNote(event: ServiceOrderTimelineItem): { label: string; text: string } | null {
  switch (event.event_type) {
    case 'schedule_cancelled':
      return event.note ? { label: 'Motivo', text: event.note } : null;
    case 'schedule_rescheduled':
    case 'timer_paused':
    case 'install_override_approved':
      return event.note ? { label: 'Observação', text: event.note } : null;
    case 'phase': {
      // note = "categoria_origem|categoria_destino|observacao" (obs opcional)
      const obs = (event.note ?? '').split('|')[2];
      return obs ? { label: 'Observação', text: obs } : null;
    }
    default:
      return null;
  }
}

/** Resolve ícone, tom e badge semântico para qualquer exibição do evento. */
export function getServiceOrderEventPresentation(type?: string): ServiceOrderEventPresentation {
  if (type === 'created') return { Icon: Plus, tone: 'bg-success/10 text-success', badgeTone: 'border-0 bg-success/15 text-success', badgeLabel: 'Criação' };
  if (type === 'status') return { Icon: Activity, tone: 'bg-warning/10 text-warning', badgeTone: 'border-0 bg-warning/15 text-warning', badgeLabel: 'Situação' };
  if (type === 'phase') return { Icon: ArrowRightLeft, tone: 'bg-primary/10 text-primary', badgeTone: 'border-0 bg-primary/15 text-primary', badgeLabel: 'Fase' };
  if (type === 'schedule') return { Icon: CalendarClock, tone: 'bg-info/10 text-info', badgeTone: 'border-0 bg-info/15 text-info', badgeLabel: 'Agenda' };
  // BR-OS-033: cancelamento em tom destrutivo; reagendamento mantém o tom informativo da agenda
  if (type === 'schedule_cancelled') return { Icon: CalendarX, tone: 'bg-destructive/10 text-destructive', badgeTone: 'border-0 bg-destructive/15 text-destructive', badgeLabel: 'Agenda cancelada' };
  if (type === 'schedule_rescheduled') return { Icon: CalendarClock, tone: 'bg-info/10 text-info', badgeTone: 'border-0 bg-info/15 text-info', badgeLabel: 'Reagendado' };
  if (type === 'timer_paused') return { Icon: Pause, tone: 'bg-warning/10 text-warning', badgeTone: 'border-0 bg-warning/15 text-warning', badgeLabel: 'Pausa' };
  if (type === 'timer_resumed') return { Icon: Play, tone: 'bg-success/10 text-success', badgeTone: 'border-0 bg-success/15 text-success', badgeLabel: 'Retomada' };
  if (type === 'checklist_stage_released') return { Icon: ClipboardCheck, tone: 'bg-primary/10 text-primary', badgeTone: 'border-0 bg-primary/15 text-primary', badgeLabel: 'Checklist' };
  if (type === 'checklist_status') return { Icon: CheckCircle2, tone: 'bg-success/10 text-success', badgeTone: 'border-0 bg-success/15 text-success', badgeLabel: 'Checklist' };
  if (type === 'provider_travel_started') return { Icon: Navigation, tone: 'bg-info/10 text-info', badgeTone: 'border-0 bg-info/15 text-info', badgeLabel: 'Deslocamento' };
  if (type === 'provider_check_in') return { Icon: MapPin, tone: 'bg-success/10 text-success', badgeTone: 'border-0 bg-success/15 text-success', badgeLabel: 'Check-in' };
  return { Icon: UserCheck, tone: 'bg-muted text-muted-foreground', badgeTone: 'border-0 bg-muted text-foreground', badgeLabel: 'Automático' };
}

/**
 * v1.3.0: localização gravada pelo app do prestador (deslocamento/check-in).
 * Reaproveita a regra pura do portal; nulo quando não há coordenadas válidas.
 */
export function getServiceOrderEventLocation(event: ServiceOrderTimelineItem): { label: string; url: string; accuracy: number | null } | null {
  const type = event.event_type as string;
  if (type !== 'provider_travel_started' && type !== 'provider_check_in') return null;
  const loc = parseEventLocation(event.new_value);
  if (!loc) return null;
  return {
    label: type === 'provider_travel_started' ? 'Local de saída registrado' : 'Local do check-in',
    url: googleMapsPointUrl(loc.lat, loc.lng),
    accuracy: loc.accuracy,
  };
}